import type { Currency } from '@/shared/models/moneyModel'
export const randomIntFromInterval = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1) + min)
}

export const isoCurrencyToSymbol = (isoCurrency: Currency, locale: string | undefined): string => {
    if (!locale) return isoCurrency
    if (!isoCurrency) return ''
    return (0)
        .toLocaleString(locale, {
            style: 'currency',
            currency: isoCurrency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        })
        .replace(/\d/g, '')
        .trim()
        .slice(0, 1)
}

export const calculateFees = (total: number | undefined, rate: number | undefined): number => {
    return rate && rate !== 0 && total ? total / rate : 0
}

/**
 * Utility object for safer decimal arithmetic operations.
 */
export const SafeMath: {
    /**
     * Gets the number of decimal places in a number.
     * @param {number|string} num - The number to check.
     * @returns {number} The number of decimal places.
     */
    getDecimalPlaces(num: number | string): number
    /**
     * Determines the maximum precision needed based on input numbers.
     * @param {...(number|string)} numbers - Numbers to compare.
     * @returns {number} The maximum number of decimal places found.
     */
    getMaxPrecision(...numbers: (number | string)[]): number
    /**
     * Rounds a number to a specified number of decimal places.
     * Uses exponential notation to avoid floating point issues.
     * Default precision is 2.
     * Implements "round half away from zero" for cases like -1.5 -> -2.
     * @param {number} num - The number to round.
     * @param {number} [precision=2] - The number of decimal places.
     * @returns {number} The rounded number, or NaN if inputs are invalid.
     */
    round(num: number, precision: number): number
    /**
     * Safely adds two numbers.
     * @param {number|string} a - First number.
     * @param {number|string} b - Second number.
     * @param {number} [precision] - Optional fixed precision for the result. If omitted, uses max precision of inputs.
     * @returns {number} The sum, or NaN if inputs are invalid.
     */
    add(a: number | string, b: number | string, precision?: number): number
    /**
     * Safely subtracts the second number from the first.
     * @param {number|string} a - First number.
     * @param {number|string} b - Second number.
     * @param {number} [precision] - Optional fixed precision for the result. If omitted, uses max precision of inputs.
     * @returns {number} The difference, or NaN if inputs are invalid.
     */
    subtract(a: number | string, b: number | string, precision?: number): number
    /**
     * Safely multiplies two numbers.
     * For multiplication, precision accumulates if not specified.
     * @param {number|string} a - First number.
     * @param {number|string} b - Second number.
     * @param {number} [precision] - Optional fixed precision for the result. If omitted, uses combined precision of inputs.
     * @returns {number} The product, or NaN if inputs are invalid.
     */
    multiply(a: number | string, b: number | string, precision?: number): number

    /**
     * Safely divides the first number by the second.
     * @param {number|string} a - Numerator.
     * @param {number|string} b - Denominator.
     * @param {number} [precision=10] - Optional fixed precision for the result. Defaults to 10 if undefined or invalid. Division often requires explicit precision.
     * @returns {number} The quotient, or NaN if inputs are invalid or division by zero.
     */
    divide(a: number | string, b: number | string, precision?: number): number
} = {
    getDecimalPlaces(num: number | string): number {
        const numStr = String(num)
        if (numStr.includes('.')) {
            const parts = numStr.split('.')
            return parts[1]?.length || 0
        }
        return 0
    },
    getMaxPrecision(...numbers: (number | string)[]): number {
        let max = 0
        numbers.forEach((num) => {
            max = Math.max(max, this.getDecimalPlaces(num))
        })
        return max
    },
    round(num: number, precision = 2): number {
        const pr = Math.max(0, Math.floor(precision))

        if (isNaN(pr) || isNaN(num)) {
            return NaN
        }

        if (!isFinite(num)) {
            return num
        }

        if (num === 0 || Object.is(num, -0)) {
            return 0
        }

        const factor = Math.pow(10, pr)
        const scaled = Number((Math.abs(num) * factor).toFixed(8))

        const rounded = Math.round(scaled)
        const result = rounded / factor

        return num < 0 ? -result : result
    },
    add(a: number | string, b: number | string, precision?: number): number {
        const numA = Number(a)
        const numB = Number(b)
        if (isNaN(numA) || isNaN(numB)) return NaN

        const resultPrecision = precision !== undefined && precision >= 0 ? precision : this.getMaxPrecision(numA, numB)

        const result = numA + numB
        return this.round(result, resultPrecision)
    },
    subtract(a: number | string, b: number | string, precision?: number): number {
        const numA = Number(a)
        const numB = Number(b)
        if (isNaN(numA) || isNaN(numB)) return NaN

        const resultPrecision = precision !== undefined && precision >= 0 ? precision : this.getMaxPrecision(numA, numB)

        const result = numA - numB
        return this.round(result, resultPrecision)
    },
    multiply(a: number | string, b: number | string, precision?: number): number {
        const numA = Number(a)
        const numB = Number(b)
        if (isNaN(numA) || isNaN(numB)) return NaN

        const resultPrecision =
            precision !== undefined && precision >= 0
                ? precision
                : this.getDecimalPlaces(numA) + this.getDecimalPlaces(numB)

        const result = numA * numB
        return this.round(result, resultPrecision)
    },
    divide(a: number | string, b: number | string, precision = 10): number {
        const numA = Number(a)
        const numB = Number(b)
        if (isNaN(numA) || isNaN(numB)) return NaN
        if (numB === 0) {
            return NaN
        }

        const resultPrecision = precision >= 0 ? precision : 10

        const result = numA / numB
        return this.round(result, resultPrecision)
    }
}

/**
 * Safely calculates the percentage of a part relative to a total.
 * @param {number} part - The numerator or portion to calculate the percentage for.
 * @param {number} total - The denominator or total value. If zero or negative, the function returns 0.
 * @returns {number} The percentage (0–100) rounded to 2 decimal places. Returns 0 if the calculation is invalid.
 */
export const getSafePercentage = (part: number, total: number): number => {
    if (total <= 0) return 0
    const ratio = SafeMath.divide(part, total)
    return isFinite(ratio) && !isNaN(ratio) ? SafeMath.multiply(ratio, 100, 2) : 0
}
